import { Trans } from "@lingui/react/macro"
import * as Sentry from "@sentry/tanstackstart-react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { AlertCircle, CheckCircle, FileUp, Loader2, XCircle, ZoomIn, ZoomOut } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { PageHeader } from "@/components/ui/PageHeader"
import { useTypedFlag } from "@/features/feature-flags/registry"
import {
  blueprintImportDebugServerFn,
  type BlueprintImportDebugResult,
} from "@/features/project-editor/upload-flow/server/blueprintImportDebug"

interface OverlayVisibility {
  shelves: boolean
  clPolygon: boolean
  storeBoundary: boolean
  colorRegions: boolean
  categoryLabels: boolean
}

type ViewMode = "uo" | "uoCropped"

export const Route = createFileRoute("/(authenticated)/projects/import-debug/")({
  component: ImportDebugPage,
})

function ImportDebugPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { value: blueprintImportEnabled, isLoading: isLoadingFlag } =
    useTypedFlag("blueprint-import")

  // Redirect to projects list if feature is disabled
  if (!isLoadingFlag && !blueprintImportEnabled) {
    navigate({ to: "/projects" })
    return null
  }

  const [clFile, setClFile] = useState<File | null>(null)
  const [uoFile, setUoFile] = useState<File | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [result, setResult] = useState<BlueprintImportDebugResult | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [imageZoom, setImageZoom] = useState(1)
  const [viewMode, setViewMode] = useState<ViewMode>("uo")
  const [overlayVisibility, setOverlayVisibility] = useState<OverlayVisibility>({
    shelves: true,
    clPolygon: true,
    storeBoundary: true,
    colorRegions: true,
    categoryLabels: true,
  })

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleProcess = async () => {
    if (!clFile || !uoFile) {
      setServerError("Both CL and UO PDF files are required")
      return
    }

    setServerError(null)
    setIsProcessing(true)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append("clPdf", clFile)
      formData.append("uoPdf", uoFile)

      const data = await blueprintImportDebugServerFn({ data: formData })
      setResult(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to process import"
      setServerError(errorMessage)
      Sentry.captureException(error)
    } finally {
      setIsProcessing(false)
    }
  }

  // Get current image based on view mode
  const getCurrentImage = useCallback(() => {
    if (!result) return { image: null, width: 0, height: 0 }
    switch (viewMode) {
      case "uo":
        return {
          image: result.uoImage,
          width: result.uoImageWidth,
          height: result.uoImageHeight,
        }
      case "uoCropped":
        return {
          image: result.uoCroppedImage,
          width: result.uoCroppedWidth ?? 0,
          height: result.uoCroppedHeight ?? 0,
        }
    }
  }, [result, viewMode])

  // Draw visualization on canvas based on view mode
  const drawVisualization = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !result) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const { image: imageBase64 } = getCurrentImage()
    if (!imageBase64) return

    const img = new Image()
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width
      canvas.height = img.height

      // Draw base image
      ctx.drawImage(img, 0, 0)

      const phase2 = result.phase2

      if (viewMode === "uo") {
        // Drawing on UO image - use UO coordinates

        // Draw store boundary (green)
        if (
          overlayVisibility.storeBoundary &&
          phase2?.storeBoundary &&
          phase2.storeBoundary.length > 2
        ) {
          ctx.strokeStyle = "#00FF00"
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(phase2.storeBoundary[0].x, phase2.storeBoundary[0].y)
          for (let i = 1; i < phase2.storeBoundary.length; i++) {
            ctx.lineTo(phase2.storeBoundary[i].x, phase2.storeBoundary[i].y)
          }
          ctx.closePath()
          ctx.stroke()
        }

        // Draw CL bounds polygon in UO coords (magenta with fill)
        // This is the full CL image bounds transformed to UO coordinates
        if (
          overlayVisibility.clPolygon &&
          result.clBoundsInUoCoords &&
          result.clBoundsInUoCoords.length > 2
        ) {
          ctx.fillStyle = "rgba(255, 0, 255, 0.2)"
          ctx.strokeStyle = "#FF00FF"
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(result.clBoundsInUoCoords[0].x, result.clBoundsInUoCoords[0].y)
          for (let i = 1; i < result.clBoundsInUoCoords.length; i++) {
            ctx.lineTo(result.clBoundsInUoCoords[i].x, result.clBoundsInUoCoords[i].y)
          }
          ctx.closePath()
          ctx.fill()
          ctx.stroke()
        }
      } else if (viewMode === "uoCropped") {
        // Drawing on cropped UO image - coordinates are already relative to crop region

        // Draw detected color overlays (phase 3) as polygons
        if (overlayVisibility.colorRegions && result.overlaysInCroppedUo) {
          for (const overlay of result.overlaysInCroppedUo) {
            if (!overlay.points || overlay.points.length < 3) continue

            ctx.fillStyle = overlay.colorHex + "20" // More transparent fill
            ctx.strokeStyle = overlay.colorHex
            ctx.lineWidth = 3 // Thicker border

            // Draw polygon from transformed corner points
            ctx.beginPath()
            ctx.moveTo(overlay.points[0].x, overlay.points[0].y)
            for (let i = 1; i < overlay.points.length; i++) {
              ctx.lineTo(overlay.points[i].x, overlay.points[i].y)
            }
            ctx.closePath()
            ctx.fill()
            ctx.stroke()
          }
        }

        // Draw shelves with their assigned colors
        if (overlayVisibility.shelves && result.shelvesWithColors) {
          for (const shelf of result.shelvesWithColors) {
            // Use assigned color if available, otherwise blue
            const color = shelf.colorHex || "#0066FF"

            // Draw filled rectangle with semi-transparent color
            ctx.fillStyle = color + "60"
            ctx.strokeStyle = color
            ctx.lineWidth = 2

            if (shelf.angle && shelf.angle !== 0) {
              ctx.save()
              ctx.translate(shelf.x + shelf.width / 2, shelf.y + shelf.height / 2)
              ctx.rotate((shelf.angle * Math.PI) / 180)
              ctx.fillRect(-shelf.width / 2, -shelf.height / 2, shelf.width, shelf.height)
              ctx.strokeRect(-shelf.width / 2, -shelf.height / 2, shelf.width, shelf.height)
              ctx.restore()
            } else {
              ctx.fillRect(shelf.x, shelf.y, shelf.width, shelf.height)
              ctx.strokeRect(shelf.x, shelf.y, shelf.width, shelf.height)
            }
          }
        }

        // Draw category labels (OCR'd merch group names with match info)
        if (overlayVisibility.categoryLabels && result.categoryLabels) {
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"

          for (const label of result.categoryLabels) {
            // Show matched code as main label, original OCR name below
            const mainText = label.matchedCode ?? label.categoryCode
            const hasMatch = label.matchedCode && label.originalOcrName !== label.matchedCode
            const originalText = hasMatch ? label.originalOcrName : null

            // Measure text for background
            ctx.font = "bold 12px sans-serif"
            const mainMetrics = ctx.measureText(mainText)
            const mainWidth = mainMetrics.width + 8
            const mainHeight = 16

            // Draw main label background
            ctx.fillStyle = label.colorHex
            ctx.fillRect(label.x - mainWidth / 2, label.y - mainHeight / 2, mainWidth, mainHeight)

            // Draw main text in white
            ctx.fillStyle = "#FFFFFF"
            ctx.fillText(mainText, label.x, label.y)

            // Draw original OCR name below if different from matched
            if (originalText) {
              ctx.font = "10px sans-serif"
              const origMetrics = ctx.measureText(originalText)
              const origWidth = origMetrics.width + 6
              const origHeight = 14
              const origY = label.y + mainHeight / 2 + origHeight / 2 + 2

              // Draw semi-transparent dark background
              ctx.fillStyle = "rgba(0, 0, 0, 0.7)"
              ctx.fillRect(label.x - origWidth / 2, origY - origHeight / 2, origWidth, origHeight)

              // Draw original text in yellow
              ctx.fillStyle = "#FFFF00"
              ctx.fillText(originalText, label.x, origY)
            }
          }
        }
      }
    }

    img.src = `data:image/png;base64,${imageBase64}`
  }, [result, overlayVisibility, viewMode, getCurrentImage])

  // Redraw when result or visibility or view mode changes
  useEffect(() => {
    drawVisualization()
  }, [drawVisualization])

  const toggleVisibility = (key: keyof OverlayVisibility) => {
    setOverlayVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const { image: currentImage, width: currentWidth, height: currentHeight } = getCurrentImage()

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <PageHeader title="Blueprint Import Debug" className="shrink-0 px-4 py-2" />

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Uploads & Results */}
        <div className="w-64 shrink-0 overflow-y-auto border-r p-4">
          <div className="space-y-4">
            {/* File Uploads */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  <Trans>Upload Files</Trans>
                </CardTitle>
                <CardDescription>
                  <Trans>Upload CL (Blueprint) and UO (Original) PDFs</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* CL PDF Upload */}
                <div
                  className={`flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed p-4 transition-colors ${
                    clFile
                      ? "border-green-500 bg-green-50 dark:bg-green-950"
                      : "hover:border-primary border-gray-300"
                  }`}
                  onClick={() => document.getElementById("cl-pdf-input")?.click()}
                >
                  <FileUp className="mb-1 h-6 w-6 text-gray-400" />
                  {clFile ? (
                    <span className="text-sm font-medium text-green-600">{clFile.name}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      <Trans>CL PDF (Blueprint with colors)</Trans>
                    </span>
                  )}
                  <input
                    id="cl-pdf-input"
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setClFile(e.target.files?.[0] || null)}
                  />
                </div>

                {/* UO PDF Upload */}
                <div
                  className={`flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed p-4 transition-colors ${
                    uoFile
                      ? "border-green-500 bg-green-50 dark:bg-green-950"
                      : "hover:border-primary border-gray-300"
                  }`}
                  onClick={() => document.getElementById("uo-pdf-input")?.click()}
                >
                  <FileUp className="mb-1 h-6 w-6 text-gray-400" />
                  {uoFile ? (
                    <span className="text-sm font-medium text-green-600">{uoFile.name}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      <Trans>UO PDF (Original floor plan)</Trans>
                    </span>
                  )}
                  <input
                    id="uo-pdf-input"
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setUoFile(e.target.files?.[0] || null)}
                  />
                </div>

                <Button
                  onClick={() => void handleProcess()}
                  disabled={isProcessing || !clFile || !uoFile}
                  className="w-full"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <Trans>Processing...</Trans>
                    </>
                  ) : (
                    <Trans>Process Import</Trans>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Error Alert */}
            {serverError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            {/* Overlay Controls */}
            {result && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    <Trans>Overlay Visibility</Trans>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {viewMode === "uo" && (
                    <>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vis-cl"
                          checked={overlayVisibility.clPolygon}
                          onCheckedChange={() => toggleVisibility("clPolygon")}
                        />
                        <Label htmlFor="vis-cl" className="flex items-center gap-2 text-sm">
                          <div className="h-3 w-3 rounded border-2 border-fuchsia-500 bg-fuchsia-500/20" />
                          <Trans>CL Bounds</Trans>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vis-boundary"
                          checked={overlayVisibility.storeBoundary}
                          onCheckedChange={() => toggleVisibility("storeBoundary")}
                        />
                        <Label htmlFor="vis-boundary" className="flex items-center gap-2 text-sm">
                          <div className="h-3 w-3 rounded border-2 border-green-500" />
                          <Trans>Store Boundary</Trans>
                        </Label>
                      </div>
                    </>
                  )}
                  {viewMode === "uoCropped" && (
                    <>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vis-colors"
                          checked={overlayVisibility.colorRegions}
                          onCheckedChange={() => toggleVisibility("colorRegions")}
                        />
                        <Label htmlFor="vis-colors" className="flex items-center gap-2 text-sm">
                          <div className="h-3 w-3 rounded border border-orange-500 bg-orange-500/30" />
                          <Trans>Detected Squares</Trans>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vis-shelves"
                          checked={overlayVisibility.shelves}
                          onCheckedChange={() => toggleVisibility("shelves")}
                        />
                        <Label htmlFor="vis-shelves" className="flex items-center gap-2 text-sm">
                          <div className="h-3 w-3 rounded bg-gradient-to-r from-red-500 via-green-500 to-blue-500" />
                          <Trans>Colored Shelves</Trans>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vis-labels"
                          checked={overlayVisibility.categoryLabels}
                          onCheckedChange={() => toggleVisibility("categoryLabels")}
                        />
                        <Label htmlFor="vis-labels" className="flex items-center gap-2 text-sm">
                          <div className="h-3 w-3 rounded bg-gray-700 text-white text-[6px] flex items-center justify-center font-bold">
                            A
                          </div>
                          <Trans>Category Labels</Trans>
                        </Label>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Phase Results */}
            {result && (
              <>
                {/* Overall Status */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <PhaseIcon success={result.success} />
                      <Trans>Import Result</Trans>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={result.success ? "default" : "destructive"}>
                      {result.success ? "Success" : "Failed"}
                    </Badge>
                    {result.error && (
                      <p className="text-destructive mt-2 text-xs">{result.error}</p>
                    )}
                    <div className="text-muted-foreground mt-1 text-xs">
                      <p>
                        UO: {result.uoImageWidth} x {result.uoImageHeight} px
                      </p>
                      {result.uoCroppedWidth && result.uoCroppedHeight && (
                        <p>
                          UO Cropped: {result.uoCroppedWidth} x {result.uoCroppedHeight} px
                        </p>
                      )}
                      {result.shelvesWithColors && (
                        <p>Shelves with colors: {result.shelvesWithColors.length}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Phase 1 Results */}
                {result.phase1 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <PhaseIcon success={result.phase1.success} />
                        <Trans>Phase 1: Locate CL</Trans>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Rotation:</span>
                        <span>{result.phase1.rotation}°</span>
                        <span className="text-muted-foreground">Confidence:</span>
                        <span>{(result.phase1.confidence * 100).toFixed(1)}%</span>
                        <span className="text-muted-foreground">Position:</span>
                        <span>
                          ({Math.round(result.phase1.matchX)}, {Math.round(result.phase1.matchY)})
                        </span>
                        <span className="text-muted-foreground">Size:</span>
                        <span>
                          {Math.round(result.phase1.matchWidth)} x{" "}
                          {Math.round(result.phase1.matchHeight)}
                        </span>
                        <span className="text-muted-foreground">Scale:</span>
                        <span>{result.phase1.matchScale.toFixed(3)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Phase 2 Results */}
                {result.phase2 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <PhaseIcon success={result.phase2.success} />
                        <Trans>Phase 2: Validate</Trans>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Shelves:</span>
                        <span>{result.phase2.shelfCount}</span>
                        <span className="text-muted-foreground">In Region:</span>
                        <span>{result.phase2.shelvesInRegion}</span>
                        <span className="text-muted-foreground">Coverage:</span>
                        <span>{result.phase2.coveragePercent.toFixed(1)}%</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Phase 3 Results */}
                {result.phase3 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <PhaseIcon success={result.phase3.success} />
                        <Trans>Phase 3: Color Regions</Trans>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Overlays:</span>
                        <span>{result.phase3.overlayCount}</span>
                        <span className="text-muted-foreground">Gaps:</span>
                        <span>{result.phase3.gapCount}</span>
                      </div>
                      {result.phase3.overlays.length > 0 && (
                        <div className="mt-2">
                          <span className="text-muted-foreground text-xs">Colors found:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {result.phase3.overlays
                              .filter((o) => !o.isGap)
                              .slice(0, 10)
                              .map((o, i) => (
                                <div
                                  key={i}
                                  className="h-4 w-4 rounded border"
                                  style={{ backgroundColor: o.colorHex }}
                                  title={o.colorHex}
                                />
                              ))}
                            {result.phase3.overlays.filter((o) => !o.isGap).length > 10 && (
                              <span className="text-muted-foreground text-xs">
                                +{result.phase3.overlays.filter((o) => !o.isGap).length - 10}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Phase 4 Results */}
                {result.phase4 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <PhaseIcon success={result.phase4.success} />
                        <Trans>Phase 4: Category Mapping</Trans>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-muted-foreground">Assigned:</span>
                        <span>{result.phase4.assignmentCount}</span>
                        <span className="text-muted-foreground">Unmapped:</span>
                        <span>{result.phase4.unmappedCount}</span>
                        {result.detectedLocale && (
                          <>
                            <span className="text-muted-foreground">Detected Locale:</span>
                            <span className="font-medium">{result.detectedLocale}</span>
                          </>
                        )}
                      </div>
                      {result.phase4.assignments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <span className="text-muted-foreground text-xs">Assignments:</span>
                          {result.phase4.assignments.slice(0, 8).map((a, i) => (
                            <div
                              key={i}
                              className="flex flex-col gap-0.5 rounded bg-gray-50 px-2 py-1 text-xs dark:bg-gray-900"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-3 w-3 shrink-0 rounded"
                                  style={{ backgroundColor: a.colorHex }}
                                />
                                <span className="font-mono font-medium">{a.categoryCode}</span>
                                <span className="text-muted-foreground">
                                  ({a.shelfCount} shelves)
                                </span>
                                {a.matchConfidence !== undefined && (
                                  <span className="text-muted-foreground ml-auto">
                                    {Math.round(a.matchConfidence * 100)}%
                                  </span>
                                )}
                              </div>
                              {a.originalCategoryCode &&
                                a.originalCategoryCode !== a.categoryCode && (
                                  <div className="text-muted-foreground flex items-center gap-1 pl-5 text-[10px]">
                                    <span>OCR:</span>
                                    <span className="font-mono text-yellow-600 dark:text-yellow-400">
                                      {a.originalCategoryCode}
                                    </span>
                                    {a.matchedDisplayName && (
                                      <span className="ml-1 truncate">
                                        ({a.matchedDisplayName})
                                      </span>
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                          {result.phase4.assignments.length > 8 && (
                            <span className="text-muted-foreground text-xs">
                              +{result.phase4.assignments.length - 8} more
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Debug Visualization */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {currentImage ? (
            <>
              {/* Controls */}
              <div className="flex shrink-0 items-center justify-between border-b px-4 py-2">
                <div className="flex items-center gap-2">
                  {/* View Mode Toggle */}
                  <div className="flex rounded-md border">
                    <Button
                      variant={viewMode === "uo" ? "default" : "ghost"}
                      size="sm"
                      className="rounded-r-none"
                      onClick={() => setViewMode("uo")}
                    >
                      UO + CL Bounds
                    </Button>
                    <Button
                      variant={viewMode === "uoCropped" ? "default" : "ghost"}
                      size="sm"
                      className="rounded-l-none"
                      onClick={() => setViewMode("uoCropped")}
                      disabled={!result?.uoCroppedImage}
                    >
                      Cropped + Shelves
                    </Button>
                  </div>
                  <span className="text-muted-foreground text-sm">
                    {currentWidth} x {currentHeight} px
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setImageZoom((z) => Math.max(0.25, z - 0.25))}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-muted-foreground w-12 text-center text-sm">
                    {Math.round(imageZoom * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setImageZoom((z) => Math.min(4, z + 0.25))}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setImageZoom(1)}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Canvas Visualization */}
              <div
                ref={containerRef}
                className="flex-1 overflow-auto bg-gray-100 p-4 dark:bg-gray-900"
              >
                <div className="relative inline-block">
                  <canvas
                    ref={canvasRef}
                    style={{
                      transform: `scale(${imageZoom})`,
                      transformOrigin: "top left",
                    }}
                    className="shadow-lg"
                  />
                  {/* SVG overlay for shelf tooltips */}
                  {viewMode === "uoCropped" && result?.shelvesWithColors && (
                    <svg
                      className="absolute left-0 top-0"
                      style={{
                        width: result.uoCroppedWidth ?? 0,
                        height: result.uoCroppedHeight ?? 0,
                        transform: `scale(${imageZoom})`,
                        transformOrigin: "top left",
                        zIndex: 10,
                        pointerEvents: "none",
                      }}
                    >
                      {result.shelvesWithColors.map((shelf) => (
                        <rect
                          key={shelf.id}
                          x={shelf.x}
                          y={shelf.y}
                          width={shelf.width}
                          height={shelf.height}
                          fill="transparent"
                          style={{ pointerEvents: "all" }}
                          transform={
                            shelf.angle
                              ? `rotate(${shelf.angle} ${shelf.x + shelf.width / 2} ${shelf.y + shelf.height / 2})`
                              : undefined
                          }
                        >
                          <title>Shelf ID: {shelf.id}</title>
                        </rect>
                      ))}
                    </svg>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-gray-400">
              <FileUp className="mb-4 h-16 w-16" />
              <p className="text-lg">
                <Trans>Upload files and process to see debug visualization</Trans>
              </p>
              <p className="text-muted-foreground text-sm">
                <Trans>
                  The visualization will show detected shelves, color regions, and boundaries
                </Trans>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function PhaseIcon({ success }: { success: boolean }) {
  return success ? (
    <CheckCircle className="h-4 w-4 text-green-500" />
  ) : (
    <XCircle className="h-4 w-4 text-red-500" />
  )
}
